import { UpdateCollectionDto } from '../contexts/useCollections';
import { CollectionQuery, Pagination, Sort } from '../models/collectionQuery.model';
import { Collection } from '../models/collection-types';
import { CollectionStatistics } from '../models/collection-types';
import { Fiber } from '../models/common-types';
import axios, { AxiosResponse } from 'axios';
import { CreateCollectionDto, MoveCollectionsDto } from '../contexts/useCollections';
import { isSuccess } from '../utils/communication';

const baseUrl = '/api/osint';

interface AddToReportBody {
    searchType: string;
    searchTerm: string;
    displaySearchTerm?: string;
}

export class collectionsAPI implements CollectionsApi {
    async fetchCollectionById(wsId: number, caseId: string, collectionId: string): Promise<any> {
        const { data } = await axios.get(`${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}`);
        return data;
    }

    async fetchCollections(
        wsId: number,
        caseId: string,
        page: Pagination,
        sort: Sort,
        types: Array<string>,
        inputTypesFilters: string[],
        watchListsFilters: any,
        find: string,
    ): Promise<{ collections: Collection[]; rowsCount: number }> {
        const collectionQuery: CollectionQuery = {
            filter: {
                analysisType: types?.join(','),
                searchType: inputTypesFilters?.join(','),
                searchTerm: find,
                watchlist: watchListsFilters,
            },
            sortBy: sort,
            page,
        };

        let response: AxiosResponse<any, any>;
        try {
            if (caseId) {
                response = await axios.post(
                    `${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/search`,
                    collectionQuery,
                );
            } else {
                response = await axios.post(`${baseUrl}/workspaces/${wsId}/collections/search`, collectionQuery);
            }
            return response.data;
        } catch (e) {
            console.log(e);
        }
        return { rowsCount: 0, collections: [] };
    }

    async moveCollectionToCase(wsId: number, caseId: string, moveCollectionsDto: MoveCollectionsDto): Promise<void> {
        return await axios.put(`${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/move`, moveCollectionsDto);
    }

    async updateCollection(
        wsId: number,
        caseId: string,
        collectionId: number,
        updateCollectionDto: UpdateCollectionDto,
    ): Promise<any> {
        return await axios.put(
            `${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}`,
            updateCollectionDto,
        );
    }

    async createCollection(wsId: number, caseId: string, collectionData: CreateCollectionDto): Promise<any> {
        return await axios.post(`${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections`, collectionData);
    }

    async deleteCollections(wsId: number, caseId: string, collectionIds: string[]): Promise<object> {
        return await Promise.all(
            collectionIds.map((id) => axios.delete(`${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/${id}`)),
        );
    }

    async duplicateCollection(wsId: number, caseId: string, collectionIds: string[]): Promise<object> {
        return await Promise.all(
            collectionIds.map((id) =>
                axios.post(`${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/${id}/duplicate`),
            ),
        );
    }

    async rerunFiber(parentId: number, fiberId: number, wsId: number, caseId: string): Promise<void> {
        await axios.get<{ fibers: Fiber[]; collectionStatistics: CollectionStatistics }>(
            `${baseUrl}/collections/${parentId}/rerunFiber/${fiberId}?wsId=${wsId}&caseId=${caseId}`,
        );
    }

    async stopFiber(parentId: number, fiberId: number, wsId: number, caseId: string): Promise<void> {
        await axios.get<{ fibers: Fiber[]; collectionStatistics: CollectionStatistics }>(
            `${baseUrl}/collections/${parentId}/stopFiber/${fiberId}?wsId=${wsId}&caseId=${caseId}`,
        );
    }

    async fetchPossibleMatches(wsId: number, caseId: string, collectionId: string): Promise<object> {
        const { data } = await axios.get(
            `${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/possibleMatches`,
        );
        return data;
    }

    async addToReport(wsId: number, caseId: string, collectionId: string, body: object): Promise<any> {
        return await axios.put(
            `${baseUrl}/workspaces/${wsId}/cases/${caseId}/collections/${collectionId}/collect`,
            body,
        );
    }

    async fetchStreams(wsId: number, caseId: string, page: number, pageSize: number) {
        let response: AxiosResponse<any, any>;
        const options = {
            sortBy: {
                order: 'DESC',
                property: 'createdAt',
            },
            page: { offset: page, limit: pageSize },
        };
        try {
            if (caseId) {
                response = await axios.post(`${baseUrl}/workspaces/${wsId}/cases/${caseId}/streams/search`, options);
            } else {
                response = await axios.post(`${baseUrl}/workspaces/${wsId}/streams/search`, options);
            }
            return response.data;
        } catch (error) {
            return [];
        }
    }

    async fetchStreamFibers(wsId: number, caseId: string, streamId: string, caseName: string) {
        try {
            const { data } = await axios.get(
                `${baseUrl}/workspaces/${wsId}/cases/${caseId}/streams/${streamId}/fibers`,
            );

            return data.map((fiberStream) => ({ ...fiberStream, isFiber: true }));
        } catch (error) {
            return [];
        }
    }

    async fetchFiberDetails(wsId: number, caseId: string, parentId: string, fiberId: string) {
        try {
            const { data } = await axios.get(
                `${baseUrl}/workspaces/${wsId}/cases/${caseId}/streams/${parentId}/fibers/${fiberId}/details`,
            );
            return data;
        } catch (error) {
            return [];
        }
    }

    async fetchCountriesByInput(input: string) {
        let result = [];
        try {
            if (input) {
                const res = await axios.get(`${baseUrl}/mapbox/countries?input=${input}`);
                if (isSuccess(res)) {
                    result = res.data;
                }
            }
            return result;
        } catch (error) {
            return [];
        }
    }

    async fetchStatesByInput(countryCode: string, input: string) {
        let result = [];
        try {
            if (input) {
                const res = await axios.get(`${baseUrl}/mapbox/states?country=${countryCode}&input=${input}`);
                if (isSuccess(res)) {
                    result = res.data;
                }
            }
            return result;
        } catch (error) {
            return [];
        }
    }

    async fetchCitiesByInput(countryCode: string, state: string, input: string) {
        let result = [];
        try {
            if (input) {
                const res = await axios.get(
                    `${baseUrl}/mapbox/cities?country=${countryCode}${state ? '&state=' + state : ''}&input=${input}`,
                );
                if (isSuccess(res)) {
                    result = res.data;
                }
            }
            return result;
        } catch (error) {
            return [];
        }
    }
}

export interface CollectionsApi {
    fetchCollectionById(wsId: number, caseId: string, collectionId: string): Promise<any>;
    fetchStreams(wsId: number, caseId: string, page: number, pageSize: number): Promise<any>;

    fetchCollections(
        wsId: number,
        caseId: string,
        page: Pagination,
        sort: Sort,
        types: Array<string>,
        inputTypesFilters: string[],
        fetchCollections: string[],
        find: string,
    ): Promise<{ collections: Collection[]; rowsCount: number }>;

    updateCollection(
        wsId: number,
        caseId: string,
        collectionId: number,
        updateCollectionDto: UpdateCollectionDto,
    ): Promise<any>;

    deleteCollections(wsId: number, caseId: string, collectionIds: string[]): Promise<object>;

    createCollection(wsId: number, caseId: string, collectionData: CreateCollectionDto): Promise<any>;

    moveCollectionToCase(wsId: number, caseId: string, moveCollectionsDto: MoveCollectionsDto): Promise<void>;

    fetchPossibleMatches(wsId: number, caseId: string, collectionId: string): Promise<object>;

    addToReport(wsId: number, caseId: string, collectionId: string, body: AddToReportBody): Promise<any>;

    fetchCountriesByInput(input: string): Promise<any>;

    fetchStatesByInput(countryCode: string, input: string): Promise<any>;

    fetchCitiesByInput(countryCode: string, state: string, input: string): Promise<any>;
}
